import { useState, useRef } from 'react'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import { FormInput } from '@/components/ui/form'

interface SummaryData {
  invoice_amount: number
  total_vendor_invoice: number
  gross_profit: number
  gross_margin: number
  customer_freight_cost: number
  customer_additional_cost: number
  customer_rebates: number
  sales_rep_commission: number
  net_profit: number
  net_margin: number
  original_amount: number
  show_rebates: boolean
}

interface SummaryTabProps {
  summary: SummaryData
  currencySymbol: string
  orderId: number
  csrfToken: string
  onNotify: (msg: string) => void
  onSaveField: (name: string, value: string) => void
}

function fmt(n: number, sym: string) {
  return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function SummaryRow({ label, value, className, editable, name, onSave, noBorder }: {
  label: string; value: string; className?: string; noBorder?: boolean
  editable?: boolean; name?: string; onSave?: (name: string, value: string) => void
}) {
  const [val, setVal] = useState(value)
  const savedRef = useRef(value)

  const handleBlur = () => {
    if (name && onSave && val !== savedRef.current) {
      onSave(name, val)
      savedRef.current = val
    }
  }

  return (
    <div className={`flex items-center justify-between py-2.5 ${noBorder ? '' : 'border-b border-zinc-100 dark:border-zinc-800'}`}>
      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">{label}</span>
      {editable ? (
        <div className="w-32">
          <FormInput
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={handleBlur}
            placeholder="0.00"
          />
        </div>
      ) : (
        <span className={`text-xs font-semibold ${className || 'text-zinc-900 dark:text-white'}`}>{value}</span>
      )}
    </div>
  )
}

export default function SummaryTab({ summary, currencySymbol: sym, onSaveField }: SummaryTabProps) {
  const [data, setData] = useState(summary)

  const save = (name: string, value: string) => {
    onSaveField(name, value)
    // Recalculate locally (approximate — server will have exact values on next load)
    setData(prev => {
      const updated = { ...prev, [name]: parseFloat(value) || 0 }
      updated.net_profit = updated.gross_profit - updated.customer_freight_cost - updated.customer_additional_cost - updated.customer_rebates - updated.sales_rep_commission
      updated.net_margin = updated.invoice_amount > 0 ? Math.round(updated.net_profit / updated.invoice_amount * 10000) / 100 : 0
      return updated
    })
  }

  const profitColor = (n: number) => n >= 0 ? 'text-emerald-600' : 'text-red-500'

  return (
    <div data-testid="summary-tab" className="max-w-xl">
      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex shrink-0 rounded-md bg-indigo-50 p-1.5 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400"><ChartBarIcon className="h-4 w-4" /></span>
            <h3 className="text-base font-semibold text-indigo-600 dark:text-indigo-400">Order Summary</h3>
          </div>
        </div>
        <div className="border-t border-zinc-100 px-5 py-1 dark:border-zinc-800">
          {/* Revenue */}
          <SummaryRow label="Customer Invoice Amount" value={fmt(data.invoice_amount, sym)} />
          <SummaryRow label="Total Vendor Invoice" value={fmt(data.total_vendor_invoice, sym)} />

          {/* Gross */}
          <SummaryRow label="Gross Profit" value={fmt(data.gross_profit, sym)} className={profitColor(data.gross_profit)} />
          <SummaryRow label="Gross Margin" value={`${data.gross_margin.toFixed(2)}%`} className={profitColor(data.gross_margin)} noBorder />
          <div className="my-1 border-t border-zinc-200 dark:border-zinc-700" />

          {/* Deductions */}
          <SummaryRow label="Inland Freight" value={fmt(data.customer_freight_cost, sym)} />
          <SummaryRow label="Additional Cost" value={fmt(data.customer_additional_cost, sym)} />
          {data.show_rebates && (
            <SummaryRow label="Rebates" value={String(data.customer_rebates)} editable name="customer_rebates" onSave={save} />
          )}
          <SummaryRow label="Sales Rep Commission" value={String(data.sales_rep_commission)} editable name="sales_rep_commission" onSave={save} noBorder />
          <div className="my-1 border-t border-zinc-200 dark:border-zinc-700" />

          {/* Net */}
          <SummaryRow label="Net Profit" value={fmt(data.net_profit, sym)} className={profitColor(data.net_profit)} />
          <SummaryRow label="Net Margin" value={`${data.net_margin.toFixed(2)}%`} className={profitColor(data.net_margin)} noBorder />
          <div className="my-1 border-t border-zinc-200 dark:border-zinc-700" />

          {/* Original */}
          <SummaryRow label="Original Order Amount" value={String(data.original_amount)} editable name="original_amount" onSave={save} />
        </div>
      </div>
    </div>
  )
}
